/**
 * Mormotsaurus Doc App - Vanilla JS modules
 * Sidebar collapse, mobile menu, TOC scroll spy, theme toggle,
 * code copy buttons, search modal, mermaid init
 */

// ─── Sidebar Collapse ───────────────────────────────────────────────
function initSidebarCollapse() {
  document.querySelectorAll<HTMLElement>('[data-doc-collapse]').forEach(toggle => {
    if (toggle.dataset.collapseInit) return; // already bound
    toggle.dataset.collapseInit = '1';

    const groupId = toggle.dataset.docCollapse || '';
    const group = document.querySelector<HTMLElement>(`[data-doc-group="${groupId}"]`);
    if (!group) return;

    // Restore from localStorage
    const stored = localStorage.getItem(`doc-nav-${groupId}`);
    if (stored === 'closed') {
      group.classList.add('hidden');
      toggle.dataset.docOpen = 'false';
    } else if (stored === 'open') {
      group.classList.remove('hidden');
      toggle.dataset.docOpen = 'true';
    }

    toggle.addEventListener('click', (e) => {
      const isOpen = toggle.dataset.docOpen === 'true';
      // Always toggle open/close
      if (isOpen) {
        group.classList.add('hidden');
        toggle.dataset.docOpen = 'false';
        localStorage.setItem(`doc-nav-${groupId}`, 'closed');
      } else {
        group.classList.remove('hidden');
        toggle.dataset.docOpen = 'true';
        localStorage.setItem(`doc-nav-${groupId}`, 'open');
      }
      // If not clicking a link, prevent default (chevron click etc.)
      if (!(e.target as HTMLElement).closest('a')) {
        e.preventDefault();
      }
    });
  });
}

// ─── Mobile Menu ─────────────────────────────────────────────────────
function initMobileMenu() {
  const menuBtn = document.getElementById('docMobileMenuBtn');
  const closeBtn = document.getElementById('docMobileMenuClose');
  const overlay = document.getElementById('docSidebarOverlay');
  const backdrop = document.getElementById('docSidebarBackdrop');

  if (!menuBtn || !overlay) return;

  const open = () => overlay.classList.remove('hidden');
  const close = () => overlay.classList.add('hidden');

  menuBtn.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  backdrop?.addEventListener('click', close);
}

function initMobileMenuGlobal() {
  document.addEventListener('keydown', (e) => {
    const overlay = document.getElementById('docSidebarOverlay');
    if (e.key === 'Escape' && overlay && !overlay.classList.contains('hidden')) {
      overlay.classList.add('hidden');
    }
  });
}

// ─── TOC Scroll Spy ──────────────────────────────────────────────────
function initTocScrollSpy() {
  const tocNav = document.getElementById('docTocNav');
  const dropdownMenu = document.getElementById('docTocDropdownMenu');
  const dropdownCurrent = document.getElementById('docTocDropdownCurrent');

  // Collect TOC links from both the sidebar TOC and the dropdown
  const sidebarLinks = tocNav
    ? Array.from(tocNav.querySelectorAll<HTMLAnchorElement>('.toc-link'))
    : [];
  const dropdownLinks = dropdownMenu
    ? Array.from(dropdownMenu.querySelectorAll<HTMLAnchorElement>('.toc-link'))
    : [];

  const allLinks = [...sidebarLinks, ...dropdownLinks];
  if (allLinks.length === 0) return;

  // Collect unique headings
  const seenIds = new Set<string>();
  const headings: HTMLElement[] = [];
  allLinks.forEach(link => {
    const id = link.getAttribute('href')?.slice(1);
    if (id && !seenIds.has(id)) {
      seenIds.add(id);
      const heading = document.getElementById(id);
      if (heading) headings.push(heading);
    }
  });

  if (headings.length === 0) return;

  // Set first heading as initial dropdown label
  if (dropdownCurrent && sidebarLinks.length > 0) {
    dropdownCurrent.textContent = sidebarLinks[0].textContent?.trim() || '';
  } else if (dropdownCurrent && dropdownLinks.length > 0) {
    dropdownCurrent.textContent = dropdownLinks[0].textContent?.trim() || '';
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          const href = `#${id}`;

          // Update sidebar TOC
          sidebarLinks.forEach(link => {
            const isActive = link.getAttribute('href') === href;
            link.classList.toggle('text-foreground', isActive);
            link.classList.toggle('font-medium', isActive);
            link.classList.toggle('text-muted-foreground', !isActive);
          });

          // Update dropdown TOC
          dropdownLinks.forEach(link => {
            const isActive = link.getAttribute('href') === href;
            link.classList.toggle('toc-dropdown-active', isActive);
          });

          // Update dropdown current label
          if (dropdownCurrent) {
            const activeLink = dropdownLinks.find(l => l.getAttribute('href') === href)
              || sidebarLinks.find(l => l.getAttribute('href') === href);
            if (activeLink) {
              dropdownCurrent.textContent = activeLink.textContent?.trim() || '';
            }
          }
        }
      });
    },
    {
      rootMargin: '-80px 0px -70% 0px',
      threshold: 0
    }
  );

  headings.forEach(h => observer.observe(h));
}

// ─── TOC Dropdown (medium screens) ──────────────────────────────────
function initTocDropdown() {
  const dropdown = document.getElementById('docTocDropdown');
  const btn = document.getElementById('docTocDropdownBtn');
  const menu = document.getElementById('docTocDropdownMenu');
  const chevron = document.getElementById('docTocDropdownChevron');

  if (!dropdown || !btn || !menu) return;

  btn.addEventListener('click', () => {
    menu.classList.toggle('hidden');
    chevron?.classList.toggle('rotate-180');
  });

  menu.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
      menu.classList.add('hidden');
      chevron?.classList.remove('rotate-180');
    });
  });
}

function initTocDropdownGlobal() {
  document.addEventListener('click', (e) => {
    const dropdown = document.getElementById('docTocDropdown');
    const menu = document.getElementById('docTocDropdownMenu');
    const chevron = document.getElementById('docTocDropdownChevron');
    if (dropdown && !dropdown.contains(e.target as Node)) {
      menu?.classList.add('hidden');
      chevron?.classList.remove('rotate-180');
    }
  });
}

// ─── Theme Toggle ────────────────────────────────────────────────────
function initThemeToggle() {
  const btn = document.getElementById('docThemeToggle');
  if (!btn) return;

  const html = document.documentElement;
  const stored = localStorage.getItem('doc-theme');
  const isDarkInitial = stored === 'dark' || (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches);

  if (isDarkInitial) {
    html.classList.add('dark');
    html.setAttribute('data-theme', 'dark');
  }

  btn.addEventListener('click', () => {
    const isDark = html.classList.contains('dark');
    if (isDark) {
      html.classList.remove('dark');
      html.setAttribute('data-theme', '');
      localStorage.setItem('doc-theme', 'light');
    } else {
      html.classList.add('dark');
      html.setAttribute('data-theme', 'dark');
      localStorage.setItem('doc-theme', 'dark');
    }
  });
}

// ─── Code Copy Buttons ───────────────────────────────────────────────
function initCodeCopyButtons() {
  document.querySelectorAll('pre').forEach(pre => {
    // Skip if already has a copy button
    if (pre.querySelector('.copy-btn')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'relative group';
    pre.parentNode?.insertBefore(wrapper, pre);
    wrapper.appendChild(pre);

    const btn = document.createElement('button');
    btn.className = 'copy-btn absolute top-2 right-2 p-1.5 rounded-md bg-background/80 border border-border text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity';
    btn.title = 'Copy code';
    btn.innerHTML = `<svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>`;

    btn.addEventListener('click', async () => {
      const code = pre.querySelector('code');
      const text = code?.textContent || pre.textContent || '';
      try {
        await navigator.clipboard.writeText(text);
        btn.innerHTML = `<svg class="h-4 w-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        setTimeout(() => {
          btn.innerHTML = `<svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>`;
        }, 2000);
      } catch { /* clipboard not available */ }
    });

    wrapper.appendChild(btn);
  });
}

// ─── Search Modal ────────────────────────────────────────────────────
let searchIndex: Array<{ slug: string; title: string; excerpt: string }> | null = null;

function createSearchModal(): HTMLElement {
  const modal = document.createElement('div');
  modal.id = 'docSearchModal';
  modal.className = 'fixed inset-0 z-50 hidden';
  modal.innerHTML = `
    <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" id="docSearchBackdrop"></div>
    <div class="fixed inset-x-0 top-[15%] mx-auto max-w-lg rounded-xl border border-border bg-popover shadow-2xl">
      <div class="flex items-center border-b border-border px-4">
        <svg class="h-4 w-4 shrink-0 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <input id="docSearchInput" type="text" placeholder="Search documentation..." class="flex-1 bg-transparent px-3 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none" autocomplete="off" />
        <kbd class="rounded border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">Esc</kbd>
      </div>
      <div id="docSearchResults" class="max-h-80 overflow-y-auto p-2">
        <p class="px-3 py-8 text-center text-sm text-muted-foreground">Type to search...</p>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  return modal;
}

function initSearch() {
  const searchBtn = document.getElementById('docSearchBtn');
  const modal = createSearchModal();
  const backdrop = document.getElementById('docSearchBackdrop');
  const input = document.getElementById('docSearchInput') as HTMLInputElement;
  const results = document.getElementById('docSearchResults')!;

  const open = async () => {
    modal.classList.remove('hidden');
    input?.focus();
    // Lazy-load search index
    if (!searchIndex) {
      try {
        const resp = await fetch('/docs/search-index.json');
        searchIndex = await resp.json();
      } catch {
        searchIndex = [];
      }
    }
  };

  const close = () => {
    modal.classList.add('hidden');
    if (input) input.value = '';
    results.innerHTML = '<p class="px-3 py-8 text-center text-sm text-muted-foreground">Type to search...</p>';
  };

  searchBtn?.addEventListener('click', open);
  backdrop?.addEventListener('click', close);

  // Ctrl+K / Cmd+K
  document.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      if (modal.classList.contains('hidden')) open();
      else close();
    }
    if (e.key === 'Escape' && !modal.classList.contains('hidden')) {
      close();
    }
  });

  // Search input handler
  input?.addEventListener('input', () => {
    const query = input.value.toLowerCase().trim();
    if (!query || !searchIndex) {
      results.innerHTML = '<p class="px-3 py-8 text-center text-sm text-muted-foreground">Type to search...</p>';
      return;
    }

    const matches = searchIndex.filter(item =>
      item.title.toLowerCase().includes(query) ||
      item.excerpt.toLowerCase().includes(query)
    );

    if (matches.length === 0) {
      results.innerHTML = '<p class="px-3 py-8 text-center text-sm text-muted-foreground">No results found.</p>';
      return;
    }

    results.innerHTML = matches.map(item => `
      <a href="/docs/${item.slug}" class="block rounded-md px-3 py-2 text-sm no-underline hover:bg-accent transition-colors">
        <div class="font-medium text-foreground">${item.title}</div>
        <div class="mt-0.5 text-xs text-muted-foreground line-clamp-2">${item.excerpt}</div>
      </a>
    `).join('');
  });

  // Keyboard navigation in results
  input?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      const first = results.querySelector('a');
      if (first) {
        window.location.href = first.getAttribute('href') || '';
      }
    }
  });
}

// ─── Smooth Scroll for Anchors ───────────────────────────────────────
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', (e) => {
      const href = anchor.getAttribute('href');
      if (!href || href === '#') return;
      const el = document.querySelector(href);
      if (!el) return;
      e.preventDefault();
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
      history.replaceState(null, '', href);
    });
  });
}

// ─── Highlight.js Init ───────────────────────────────────────────────
function initHighlightJs() {
  if (typeof (window as any).hljs !== 'undefined') {
    document.querySelectorAll<HTMLElement>('pre code:not(.no-highlight)').forEach(block => {
      (window as any).hljs.highlightElement(block);
    });
  }
}

// ─── Mermaid Init ────────────────────────────────────────────────────
function initMermaid() {
  if (typeof (window as any).mermaid === 'undefined') return;
  const isDark = document.documentElement.classList.contains('dark');
  (window as any).mermaid.initialize({
    startOnLoad: false,
    theme: isDark ? 'dark' : 'default'
  });
  // Convert <pre><code class="mermaid"> to <div class="mermaid">
  // textContent decodes HTML entities (e.g. &gt; back to >)
  const nodes: Element[] = [];
  document.querySelectorAll('code.mermaid, code.no-highlight').forEach(code => {
    const pre = code.parentElement;
    if (!pre || pre.tagName !== 'PRE') return;
    const div = document.createElement('div');
    div.className = 'mermaid';
    div.textContent = code.textContent || '';
    pre.replaceWith(div);
    nodes.push(div);
  });
  if (nodes.length > 0) {
    (window as any).mermaid.run({ nodes });
  }
}

// ─── Heading Anchor Links ───────────────────────────────────────────
function initHeadingAnchors() {
  document.querySelectorAll<HTMLElement>('h2[id], h3[id], h4[id]').forEach(heading => {
    const anchor = document.createElement('a');
    anchor.className = 'heading-anchor';
    anchor.href = `#${heading.id}`;
    anchor.textContent = '#';
    heading.appendChild(anchor);
  });
}

// ─── Re-init after Alpine Ajax swap ─────────────────────────────────
function reinitSwappedContent() {
  initSidebarCollapse();
  initMobileMenu();
  initTocDropdown();
  initTocScrollSpy();
  initCodeCopyButtons();
  initHeadingAnchors();
  initSmoothScroll();
  initMermaid();
  initHighlightJs();
}

// ─── Init All ────────────────────────────────────────────────────────
let _firstLoad = true;

// Called by Alpine x-init on #doc-body — runs on every swap
(window as any).onDocBodyInit = function () {
  if (_firstLoad) {
    _firstLoad = false;
    return; // DOMContentLoaded already handled first init
  }
  reinitSwappedContent();
  window.scrollTo({ top: 0, behavior: 'instant' });
};

document.addEventListener('DOMContentLoaded', () => {
  // One-time global setup (document-level listeners)
  initMobileMenuGlobal();
  initTocDropdownGlobal();
  initThemeToggle();
  initSearch();

  // Element-level setup (also called on swap via x-init)
  reinitSwappedContent();
});
